import React from "react";
import styled from "@emotion/styled";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PushPin from "@mui/icons-material/PushPin";
import UndoIcon from "@mui/icons-material/Undo";
import { Button, Menu, MenuItem } from "@mui/material";
import { SidekickTheme } from "@avenue-8/sidekick-ui";

const MenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: initial;
  max-width: initial;
  min-width: initial;
  padding: 0.25rem 0.5rem 0.25rem 0.5rem;
  margin-right: 0.25rem;
  color: ${(p) => p.theme.text.colors.primary};
  height: 100%;

  &:hover {
    opacity: 1;
    background-color: ${(p) => p.theme.colors.background3};
  }
`;

const getMenuItemColor = (
  theme: SidekickTheme,
  variant?: "primary" | "danger"
) => {
  const colors = {
    primary: theme.text.colors.primary,
    danger: theme.colors.error,
  };
  return colors[variant ?? "primary"];
};

const CustomMenuItem = styled(MenuItem)<{ variant?: "primary" | "danger" }>`
  color: ${(p) => getMenuItemColor(p.theme, p.variant)};
`;

type Props = {
  onRenameClick: () => void;
  onDeleteClick: () => void;
  onPinClick: () => void;
  onUnpinClick: () => void;
  pinned: boolean;
};

export const ThreadActionsMenu = (props: Props) => {
  const { onRenameClick, onDeleteClick, onPinClick, onUnpinClick, pinned } =
    props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getMenuItemClick =
    (onClick: () => void) => (e: React.MouseEvent<HTMLLIElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onClick();
      handleClose();
    };

  return (
    <div>
      <MenuButton
        id="thread-actions-button"
        aria-controls={open ? "thread-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="text"
      >
        <MoreHorizIcon />
      </MenuButton>
      <Menu
        id="thread-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        autoFocus={false}
        MenuListProps={{
          "aria-labelledby": "thread-actions-button",
        }}
      >
        <CustomMenuItem onClick={getMenuItemClick(onRenameClick)}>
          <EditIcon fontSize="small" /> Rename
        </CustomMenuItem>
        {pinned ? (
          <CustomMenuItem
            onClick={getMenuItemClick(onUnpinClick)}
            variant="primary"
          >
            <UndoIcon fontSize="small" /> Unpin chat
          </CustomMenuItem>
        ) : (
          <CustomMenuItem
            onClick={getMenuItemClick(onPinClick)}
            variant="primary"
          >
            <PushPin fontSize="small" /> Pin chat
          </CustomMenuItem>
        )}
        <CustomMenuItem onClick={getMenuItemClick(onDeleteClick)}>
          <DeleteIcon fontSize="small" /> Delete
        </CustomMenuItem>
      </Menu>
    </div>
  );
};
